import type { TFunction } from "i18next";
import {
  storageService,
  StorageUploadError,
  type EntityType,
  type StorageUploadProgress,
} from "@/services/storageService";
import type { TicketAttachmentCreate } from "@/services/supportTicketService";
import {
  FILE_SIZE_BYTES,
  formatFileSizeLimit,
  getFileKind,
} from "@/lib/fileValidation";
import { storagePathFromValue } from "@/lib/storageFetch";

export type { StorageUploadProgress } from "@/services/storageService";
export { StorageUploadError };

export type ActiveStorageUpload = StorageUploadProgress & {
  fileName: string;
  fileIndex: number;
  fileCount: number;
};

export function getStorageUploadErrorMessage(
  error: unknown,
  t: TFunction<"components">,
  fileName?: string,
): string {
  if (error instanceof StorageUploadError && error.status === 413) {
    return t("storageUploadTooLarge", {
      max: formatFileSizeLimit(FILE_SIZE_BYTES.STORAGE_OBJECT),
    });
  }

  if (fileName) {
    return t("storageUploadFailedNamed", { name: fileName });
  }
  return t("storageUploadFailed");
}

export async function uploadStorageFiles(
  entityType: EntityType,
  entityId: string,
  files: File[],
  onProgress?: (upload: ActiveStorageUpload) => void,
): Promise<string[]> {
  const urls: string[] = [];
  for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
    const file = files[fileIndex];
    const url = await storageService.uploadFile(entityType, entityId, file, {
      onProgress: (state) => {
        onProgress?.({
          ...state,
          fileName: file.name,
          fileIndex,
          fileCount: files.length,
        });
      },
    });
    urls.push(url);
  }
  return urls;
}

export async function uploadSupportTicketAttachments(
  entityId: string,
  files: File[],
  onProgress?: (upload: ActiveStorageUpload) => void,
): Promise<TicketAttachmentCreate[]> {
  const urls = await uploadStorageFiles(
    "support-tickets",
    entityId,
    files,
    onProgress,
  );
  return urls.map((url, index) => toTicketAttachment(files[index], url));
}

function toTicketAttachment(
  file: File,
  uploadedUrl: string,
): TicketAttachmentCreate {
  return {
    file_name: file.name,
    storage_path: storagePathFromValue(uploadedUrl),
    file_type: getFileKind(file),
    file_size: file.size,
    mime_type: file.type,
  };
}
